import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { UnsavedDecisionDialog } from "@/components/UnsavedDecisionDialog";

/**
 * Global "unsaved changes" guard.
 *
 * Any edit form registers its current dirty state + a save() callback via the
 * `useUnsavedChanges(dirty, save)` hook. When the user tries to navigate to
 * another in-app page (or close/refresh the tab) while something is dirty, a
 * modal asks whether to save, leave without saving, or stay.
 *
 * Requires a data router (createBrowserRouter) for useBlocker to work.
 */
const Ctx = createContext(null);

export function UnsavedChangesProvider({ children }) {
  const registry = useRef(new Map()); // id -> { dirty, save, discard, prompt }
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const anyDirty = useCallback(() => {
    for (const v of registry.current.values()) if (v.dirty) return true;
    return false;
  }, []);

  const register = useCallback((id, dirty, save, discard, prompt) => {
    registry.current.set(id, { dirty, save, discard, prompt });
  }, []);
  const unregister = useCallback((id) => { registry.current.delete(id); }, []);

  // Save every dirty form. Returns false if any save aborts (e.g. validation).
  const saveAll = useCallback(async () => {
    // A save changes React state: hooks unregister/re-register during the await.
    // Iterating the live Map would visit the re-inserted form again and POST twice.
    const forms = Array.from(registry.current.values()).filter(v => v.dirty);
    for (const v of forms) {
      if (v.dirty && typeof v.save === "function") {
        const ok = await v.save();
        if (ok === false) return false;
      }
    }
    return true;
  }, []);

  // Revert every dirty form to its last-saved state (in-place, no navigation).
  const discardAll = useCallback(() => {
    for (const v of Array.from(registry.current.values())) {
      if (v.dirty && typeof v.discard === "function") v.discard();
    }
  }, []);

  const blocker = useBlocker(() => anyDirty());

  // Warn on hard reload / tab close.
  useEffect(() => {
    const handler = (e) => {
      if (anyDirty()) { e.preventDefault(); e.returnValue = ""; return ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [anyDirty]);

  const stay = () => { if (blocker.state === "blocked") blocker.reset(); };
  const leave = () => { if (blocker.state === "blocked") blocker.proceed(); };
  const saveAndLeave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (await saveAll() === false) return; // validation aborted -> stay
      if (blocker.state === "blocked") blocker.proceed();
    } catch {
      // Unexpected save failure -> stay on page (modal closes via reset).
      if (blocker.state === "blocked") blocker.reset();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Ctx.Provider value={{ register, unregister, anyDirty, saveAll, discardAll }}>
      {children}
      {blocker.state === "blocked" && (
        <UnsavedDecisionDialog
          prompt={Array.from(registry.current.values()).find(v => v.dirty)?.prompt}
          busy={saving} onStay={stay} onDiscard={leave} onSave={saveAndLeave}
          testIds={{ modal: "unsaved-changes-modal", description: "unsaved-changes-description", error: "unsaved-changes-error",
            stay: "unsaved-stay-btn", discard: "unsaved-leave-btn", save: "unsaved-save-btn" }}
        />
      )}
    </Ctx.Provider>
  );
}

/**
 * Register a form's dirty state + save callback with the global guard.
 * `save` may return `false` to abort the "save & leave" flow (e.g. validation
 * error), keeping the user on the page.
 */
export function useUnsavedChanges(dirty, save, discard, prompt) {
  const ctx = useContext(Ctx);
  const id = useId();
  // Re-register every render so the guard always reads the latest dirty/save.
  useEffect(() => {
    if (!ctx) return undefined;
    ctx.register(id, dirty, save, discard, prompt);
    return () => ctx.unregister(id);
  });
}

/**
 * Access the global unsaved-changes controller. Lets a page (e.g. the product
 * editor) gate its own in-page tab switches: check `anyDirty()`, then run
 * `saveAll()` / `discardAll()` based on the user's choice.
 */
export function useUnsavedControls() {
  return useContext(Ctx);
}
